import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, KeyRound, Landmark, LockKeyhole } from "lucide-react";
import { Modal } from "./Modal";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { SUITES } from "../../data/content";
import { Loader2, ShieldCheck, MailCheck, Hash, KeyRound as KeyIcon } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export const AutonomousSuiteModal = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose} wide testId="autonomous-suite-modal">
    <div className="p-8 md:p-12">
      <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Autonomous Practice Suites</p>
      <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">
        Independence, Engineered — N.J.A.C. 13:35-6.17
      </h2>
      <p className="mt-4 text-[#64748B] leading-relaxed">
        Purpose-built advisory and operational suites for APRNs advancing to autonomous practice — from formation
        through fully independent operations, governed by New Jersey regulatory mechanics.
      </p>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {SUITES.map((s) => {
          const C = Icons[s.icon] || Landmark;
          return (
            <div key={s.title} className="border border-[#E2E8F0] bg-[#F8FAFC] rounded-xl p-6">
              <C size={22} className="text-[#D4AF37]" />
              <h3 className="mt-4 font-serif text-lg text-[#0B1B2B] font-semibold leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </Modal>
);

export const DemoCredentialModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", area_of_interest: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const closeAll = () => {
    onClose();
    setPending(null);
    setVerified(null);
    setCode("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/demo-request", form);
      setPending(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/demo-request/verify", { request_id: pending.request_id, code });
      setVerified(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={closeAll} testId="demo-credential-modal">
      <div className="p-8 md:p-10">
        {verified ? (
          <div className="text-center py-4" data-testid="demo-request-confirmation">
            <ShieldCheck size={32} className="text-[#D4AF37] mx-auto" />
            <h2 className="mt-4 font-serif text-2xl text-[#0B1B2B]">Authentication Complete</h2>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed" data-testid="demo-sla-notice">
              {verified.message}
            </p>
            <div className="mt-4 font-serif text-lg font-bold text-[#0B1B2B]" data-testid="demo-reference-id">{verified.reference_id}</div>
          </div>
        ) : pending ? (
          <>
            <MailCheck size={28} className="text-[#D4AF37]" />
            <h2 className="mt-3 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">Authenticate Your Email</h2>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">
              We've sent an email to <span className="font-semibold text-[#0B1B2B]">{form.email}</span> containing
              two different things — a Reference ID (for your records) and an Authentication Code (to type in below).
              They are not the same number.
            </p>

            {/* Reference ID — informational only, visually distinct from the action area below */}
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="demo-pending-reference-id">
              <Hash size={18} className="text-[#94A3B8] mt-0.5 shrink-0" />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#94A3B8]">Reference ID · for your records</div>
                <div className="mt-0.5 font-mono text-sm font-bold text-[#0B1B2B]">{pending.reference_id}</div>
                <div className="mt-1 text-xs text-[#94A3B8]">This identifies your request. You do not need to enter it anywhere.</div>
              </div>
            </div>

            {/* Authentication code — the actionable step, visually separated and emphasized */}
            <form onSubmit={verify} className="mt-5 space-y-4">
              <div className="rounded-xl border-2 border-[#D4AF37] bg-[#FDF8ED] p-5">
                <div className="flex items-center gap-2">
                  <KeyIcon size={16} className="text-[#8A6A12]" />
                  <label htmlFor="demo-code-input" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8A6A12]">
                    Enter your 6-digit Authentication Code here
                  </label>
                </div>
                <p className="mt-1.5 text-xs text-[#64748B]">This is the code from the email — not the Reference ID above. It expires in 15 minutes.</p>
                <div className="mt-4 flex justify-center">
                  <InputOTP
                    id="demo-code-input"
                    maxLength={6}
                    inputMode="numeric"
                    value={code}
                    onChange={(v) => setCode(v.replace(/\D/g, ""))}
                    data-testid="demo-code-input"
                  >
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          className="h-12 w-11 text-xl font-bold border-[#D4AF37]/60 bg-white"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="demo-error">{error}</p>}
              <button type="submit" disabled={busy || code.length !== 6} className="w-full bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="demo-verify-button">
                {busy && <Loader2 size={16} className="animate-spin" />}
                Authenticate & Release Credentials
              </button>
            </form>
          </>
        ) : (
          <>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Demo Access</p>
            <h2 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight">Request Demo Subscriber Credentials</h2>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input className={inputCls} placeholder="Full Name" required value={form.name} onChange={set("name")} data-testid="demo-name-input" />
              <input className={inputCls} placeholder="Organization" required value={form.organization} onChange={set("organization")} data-testid="demo-org-input" />
              <input className={inputCls} type="email" placeholder="Work Email" required pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}" title="Enter a valid institutional email address" value={form.email} onChange={set("email")} data-testid="demo-email-input" />
              <input className={inputCls} type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="demo-phone-input" />
              <select className={inputCls} required value={form.area_of_interest} onChange={set("area_of_interest")} data-testid="demo-interest-select">
                <option value="">Area of Interest…</option>
                {["University Placement Engine", "FQHC / Health System Partnership", "Pediatric Clinical Pods", "Homecare Workforce", "Vocational Nursing Clinical Infrastructure", "Preceptor Faculty Network", "Global USCE Clerkships"].map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              {error && <p className="text-sm text-red-600" data-testid="demo-error">{error}</p>}
              <button type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="demo-submit-button">
                {busy && <Loader2 size={16} className="animate-spin" />}
                Submit Credential Request
              </button>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export const Hero = ({ onOpenAuth, onOpenDemo, onApplyPreceptor }) => {
  const scrollTo = (t) => document.querySelector(t)?.scrollIntoView({ behavior: "smooth" });
  const trackBucket = (bucket) => api.post("/track/bucket", { bucket }).catch(() => {});
  return (
    <section id="hero" className="relative bg-[#0B1B2B] pt-[76px] overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{ backgroundImage: "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)", backgroundSize: "56px 56px" }}
      />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.10), transparent 55%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 lg:grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-10">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-6">
            Acuity Rise Professional Services · Clinical Placement Partners
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.08]" data-testid="hero-headline">
            Reliable <span className="text-[#D4AF37] italic">Clinical Placements</span> for the Programs Training Tomorrow's Healthcare Workforce
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }} className="mt-8 text-base md:text-lg text-slate-300 font-light max-w-3xl leading-relaxed">
            Nursing and health programs everywhere face the same problem: there aren't enough clinical sites or
            preceptors to train the students who need them. Acuity Rise closes that gap. We match qualified
            preceptors and vetted clinical sites to your students, and we take care of the credentialing,
            compliance paperwork, and hands-on EHR training — so every student and clinician arrives prepared
            from day one.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mt-5 text-sm md:text-base text-slate-400 font-light max-w-3xl leading-relaxed">
            Schools and programs get guaranteed placement capacity for every cohort. Host facilities welcome
            students without adding supervision burden — and without giving up revenue.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.36 }} className="mt-10 p-5 rounded-xl border border-[#D4AF37]/30 bg-white/[0.04] max-w-3xl" data-testid="hero-role-split">
            <p className="text-xs tracking-[0.15em] uppercase text-[#D4AF37] font-semibold mb-1">Not sure where to start? Tell us who you are.</p>
            <p className="text-xs text-slate-400 mb-3">Each option opens a short overview built for you, then brings you back here to register.</p>
            <div className="flex flex-wrap gap-3">
              <a href="https://acuity-rise-partners.pplx.app/universities.html" target="_blank" rel="noopener" onClick={() => trackBucket("universities")} className="bg-white text-[#0B1B2B] font-semibold px-5 py-3 rounded-xl hover:bg-slate-100 transition-all duration-300 flex items-center gap-2" data-testid="hero-role-universities-button">
                Universities & Nursing Programs
                <ArrowRight size={15} />
              </a>
              <button onClick={() => { trackBucket("facilities"); scrollTo("#stakeholder-grid"); }} className="text-white font-medium px-5 py-3 rounded-xl border border-white/25 hover:border-[#D4AF37]/70 hover:bg-white/5 transition-all duration-300 flex items-center gap-2" data-testid="hero-role-facilities-button">
                Healthcare Facilities & Health Systems
                <ArrowRight size={15} />
              </button>
              <a href="https://acuity-rise-partners.pplx.app/vocational-nursing.html" target="_blank" rel="noopener" onClick={() => trackBucket("vocational")} className="text-white font-medium px-5 py-3 rounded-xl border border-white/25 hover:border-[#D4AF37]/70 hover:bg-white/5 transition-all duration-300 flex items-center gap-2" data-testid="hero-role-vocational-button">
                Vocational & LPN Schools
                <ArrowRight size={15} />
              </a>
              <a href="https://acuity-rise-partners.pplx.app/preceptors.html" target="_blank" rel="noopener" onClick={() => trackBucket("preceptors")} className="bg-white text-[#0B1B2B] font-semibold px-5 py-3 rounded-xl hover:bg-slate-100 transition-all duration-300 flex items-center gap-2" data-testid="hero-role-preceptors-button">
                Preceptors & Clinicians
                <ArrowRight size={15} />
              </a>
              <a href="https://acuity-rise-partners.pplx.app/global-usce.html" target="_blank" rel="noopener" onClick={() => trackBucket("global")} className="text-white font-medium px-5 py-3 rounded-xl border border-white/25 hover:border-[#D4AF37]/70 hover:bg-white/5 transition-all duration-300 flex items-center gap-2" data-testid="hero-role-global-button">
                Global USCE / IMG Pathways
                <ArrowRight size={15} />
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }} className="mt-6 flex flex-wrap gap-4">
            <button onClick={() => scrollTo("#stakeholder-grid")} className="group bg-[#D4AF37] text-[#0B1B2B] font-semibold px-7 py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 hover:shadow-xl flex items-center gap-2" data-testid="hero-explore-pathways-button">
              Explore Stakeholder Pathways
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={onOpenAuth} className="text-white font-medium px-7 py-3.5 rounded-xl border border-white/25 hover:border-[#D4AF37]/70 hover:bg-white/5 transition-all duration-300 flex items-center gap-2" data-testid="hero-subscriber-login-button">
              <LockKeyhole size={16} className="text-[#D4AF37]" />
              Subscriber Login
            </button>
            <button onClick={onOpenDemo} className="text-[#D4AF37] font-medium px-7 py-3.5 rounded-xl border border-[#D4AF37]/60 hover:bg-[#D4AF37]/10 transition-all duration-300 flex items-center gap-2" data-testid="hero-demo-credentials-button">
              <KeyRound size={15} />
              Request Demo Credentials
            </button>
          </motion.div>
        </div>
      </div>
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[["256-Bit", "Encrypted Operations Vault"], ["HIPAA · FERPA", "Aligned Data Governance"], ["Epic · Cerner · eCW", "Pre-Deployment EHR Rigor"], ["Committed", "Placement Capacity Model"]].map(([v, l]) => (
            <div key={l} className="border-l-2 border-[#D4AF37]/50 pl-4">
              <div className="font-serif text-2xl text-white">{v}</div>
              <div className="text-xs tracking-wide uppercase text-slate-400 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
